// Oracle P6 XML Parser
// Parses P6 XML export format into structured TypeScript objects

export interface P6Calendar {
  objectId: number;
  name: string;
  hoursPerDay: number;
}

export type P6ActivityType =
  | 'Task Dependent'
  | 'Start Milestone'
  | 'Finish Milestone'
  | 'Level of Effort'
  | 'WBS Summary';
export type P6ActivityStatus = 'Completed' | 'In Progress' | 'Not Started';
export type P6RelationshipType =
  | 'Finish to Start'
  | 'Start to Start'
  | 'Finish to Finish'
  | 'Start to Finish';

export interface P6Activity {
  objectId: number;
  id: string;
  name: string;
  type: P6ActivityType;
  status: P6ActivityStatus;
  calendarObjectId: number;
  plannedDuration: number; // hours
  remainingDuration: number; // hours
  atCompletionDuration: number; // hours
  earlyStartDate: string | null;
  earlyFinishDate: string | null;
  lateStartDate: string | null;
  lateFinishDate: string | null;
  primaryConstraintType: string | null;
  primaryConstraintDate: string | null;
  wbsObjectId: number | null;
}

export interface P6Relationship {
  objectId: number;
  predecessorActivityObjectId: number;
  successorActivityObjectId: number;
  type: P6RelationshipType;
  lag: number; // hours
}

export interface P6WBS {
  objectId: number;
  code: string;
  name: string;
}

export interface P6Project {
  name: string;
  dataDate: string;
  plannedStartDate: string;
  scheduledFinishDate: string;
  calendars: Map<number, P6Calendar>;
  activities: P6Activity[];
  relationships: P6Relationship[];
  wbs: P6WBS[];
}

function getTextContent(parent: Element, tagName: string, ns?: string | null): string | null {
  // Try namespace-aware first, then fall back to local name
  let elements: HTMLCollectionOf<Element> | NodeListOf<Element>;
  if (ns) {
    elements = parent.getElementsByTagNameNS(ns, tagName);
  } else {
    elements = parent.getElementsByTagName(tagName);
  }

  // Only get direct children to avoid picking up nested element values
  for (let i = 0; i < elements.length; i++) {
    if (elements[i].parentElement === parent) {
      return elements[i].textContent?.trim() || null;
    }
  }
  return null;
}

function getNumber(parent: Element, tagName: string, ns?: string | null, defaultVal = 0): number {
  const text = getTextContent(parent, tagName, ns);
  if (text === null || text === '') return defaultVal;
  const num = parseFloat(text);
  return isNaN(num) ? defaultVal : num;
}

function detectNamespace(doc: Document): string | null {
  // Check root element namespace
  const root = doc.documentElement;
  if (root.namespaceURI && root.namespaceURI.includes('Primavera')) {
    return root.namespaceURI;
  }
  // Check if default P6 namespace is used
  if (root.getAttribute('xmlns')?.includes('Primavera')) {
    return root.getAttribute('xmlns');
  }
  return null;
}

function getElementsByTag(
  parent: Element | Document,
  tagName: string,
  ns: string | null
): Element[] {
  let elements: HTMLCollectionOf<Element>;
  if (ns) {
    elements = parent.getElementsByTagNameNS(ns, tagName);
  } else {
    elements = parent.getElementsByTagName(tagName);
  }
  return Array.from(elements);
}

export function parseP6XML(xmlString: string): P6Project {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xmlString, 'text/xml');

  // Check for parse errors
  const parseError = doc.querySelector('parsererror');
  if (parseError) {
    throw new Error(`XML Parse Error: ${parseError.textContent}`);
  }

  const ns = detectNamespace(doc);

  // Find the Project element
  const projectElements = getElementsByTag(doc, 'Project', ns);
  if (projectElements.length === 0) {
    throw new Error(
      'No <Project> element found in the XML file. Please upload a valid P6 XML export.'
    );
  }
  const projectEl = projectElements[0];

  // Parse project metadata
  const projectName =
    getTextContent(projectEl, 'Name', ns) ||
    getTextContent(projectEl, 'Id', ns) ||
    'Unknown Project';
  const dataDate = getTextContent(projectEl, 'DataDate', ns) || '';
  const plannedStartDate = getTextContent(projectEl, 'PlannedStartDate', ns) || '';
  const scheduledFinishDate = getTextContent(projectEl, 'ScheduledFinishDate', ns) || '';

  // Parse calendars
  const calendars = new Map<number, P6Calendar>();
  const calendarElements = getElementsByTag(projectEl, 'Calendar', ns);
  for (const calEl of calendarElements) {
    // Only process direct Calendar children of Project (not nested StandardWorkWeek etc)
    if (calEl.parentElement !== projectEl) continue;
    const objectId = getNumber(calEl, 'ObjectId', ns);
    const name = getTextContent(calEl, 'Name', ns) || '';
    const hoursPerDay = getNumber(calEl, 'HoursPerDay', ns, 8);
    calendars.set(objectId, { objectId, name, hoursPerDay });
  }

  // Default calendar if none found
  if (calendars.size === 0) {
    calendars.set(0, { objectId: 0, name: 'Default', hoursPerDay: 8 });
  }

  // Parse WBS
  const wbs: P6WBS[] = [];
  const wbsElements = getElementsByTag(projectEl, 'WBS', ns);
  for (const wbsEl of wbsElements) {
    if (wbsEl.parentElement !== projectEl) continue;
    wbs.push({
      objectId: getNumber(wbsEl, 'ObjectId', ns),
      code: getTextContent(wbsEl, 'Code', ns) || '',
      name: getTextContent(wbsEl, 'Name', ns) || '',
    });
  }

  // Parse activities
  const activities: P6Activity[] = [];
  const activityElements = getElementsByTag(projectEl, 'Activity', ns);
  for (const actEl of activityElements) {
    if (actEl.parentElement !== projectEl) continue;

    const type = (getTextContent(actEl, 'Type', ns) || 'Task Dependent') as P6ActivityType;
    const status = (getTextContent(actEl, 'Status', ns) || 'Not Started') as P6ActivityStatus;

    activities.push({
      objectId: getNumber(actEl, 'ObjectId', ns),
      id: getTextContent(actEl, 'Id', ns) || '',
      name: getTextContent(actEl, 'Name', ns) || '',
      type,
      status,
      calendarObjectId: getNumber(actEl, 'CalendarObjectId', ns),
      plannedDuration: getNumber(actEl, 'PlannedDuration', ns),
      remainingDuration: getNumber(actEl, 'RemainingDuration', ns),
      atCompletionDuration: getNumber(actEl, 'AtCompletionDuration', ns),
      earlyStartDate: getTextContent(actEl, 'EarlyStartDate', ns),
      earlyFinishDate: getTextContent(actEl, 'EarlyFinishDate', ns),
      lateStartDate: getTextContent(actEl, 'LateStartDate', ns),
      lateFinishDate: getTextContent(actEl, 'LateFinishDate', ns),
      primaryConstraintType: getTextContent(actEl, 'PrimaryConstraintType', ns),
      primaryConstraintDate: getTextContent(actEl, 'PrimaryConstraintDate', ns),
      wbsObjectId: getTextContent(actEl, 'WBSObjectId', ns)
        ? getNumber(actEl, 'WBSObjectId', ns)
        : null,
    });
  }

  // Parse relationships
  const relationships: P6Relationship[] = [];
  const relElements = getElementsByTag(projectEl, 'Relationship', ns);
  for (const relEl of relElements) {
    if (relEl.parentElement !== projectEl) continue;
    relationships.push({
      objectId: getNumber(relEl, 'ObjectId', ns),
      predecessorActivityObjectId: getNumber(relEl, 'PredecessorActivityObjectId', ns),
      successorActivityObjectId: getNumber(relEl, 'SuccessorActivityObjectId', ns),
      type: (getTextContent(relEl, 'Type', ns) || 'Finish to Start') as P6RelationshipType,
      lag: getNumber(relEl, 'Lag', ns),
    });
  }

  return {
    name: projectName,
    dataDate,
    plannedStartDate,
    scheduledFinishDate,
    calendars,
    activities,
    relationships,
    wbs,
  };
}
